import { $api } from "@/kibi/core/utils/api";
import type { InjectionKey } from "vue";
import type {
  AuthResponseDTO,
  ForgotPasswordRequestDTO,
  LoginRequestDTO,
  LogoutRequestDTO,
  RefreshTokenRequestDTO,
  ResendOTPRequestDTO,
  SwitchAccountRequestDTO,
  UpdatePasswordRequestDTO,
  UserDTO,
  ValidateOTPRequestDTO,
  ValidateRecoveryCodeRequestDTO,
  VerifyOTPRequestDTO,
} from "../models/auth_model";

export interface AuthDataSource {
  login(request: LoginRequestDTO): Promise<AuthResponseDTO>;
  verifyOTP(request: VerifyOTPRequestDTO): Promise<AuthResponseDTO>;
  forgotPassword(request: ForgotPasswordRequestDTO): Promise<void>;
  updatePassword(request: UpdatePasswordRequestDTO): Promise<void>;
  resendOTP(request: ResendOTPRequestDTO): Promise<void>;
  validateResetToken(token: string): Promise<{ email: string }>;
  refreshToken(request: RefreshTokenRequestDTO): Promise<AuthResponseDTO>;
  logout(request: LogoutRequestDTO): Promise<void>;
  switchAccount(request: SwitchAccountRequestDTO): Promise<AuthResponseDTO>;
  fetchMe(): Promise<UserDTO>;
  // Deprecated: Use verifyOTP with method field instead
  validateTOTP(request: ValidateOTPRequestDTO): Promise<AuthResponseDTO>;
  // Deprecated: Use verifyOTP with method field instead
  validateRecoveryCode(
    request: ValidateRecoveryCodeRequestDTO,
  ): Promise<AuthResponseDTO>;
}

export const authDataSourceKey = Symbol(
  "AuthDataSource",
) as InjectionKey<AuthDataSource>;

export class AuthDataSourceImpl implements AuthDataSource {
  async login(request: LoginRequestDTO): Promise<AuthResponseDTO> {
    const response = await $api<{ message: string; data: AuthResponseDTO }>(
      "/auth/login",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async verifyOTP(request: VerifyOTPRequestDTO): Promise<AuthResponseDTO> {
    const response = await $api<{ message: string; data: AuthResponseDTO }>(
      "/auth/verify-otp",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async forgotPassword(request: ForgotPasswordRequestDTO): Promise<void> {
    await $api("/auth/forgot-password", {
      method: "POST",
      body: request,
    });
  }

  async updatePassword(request: UpdatePasswordRequestDTO): Promise<void> {
    await $api("/auth/update-password", {
      method: "POST",
      body: request,
    });
  }

  async resendOTP(request: ResendOTPRequestDTO): Promise<void> {
    await $api("/auth/resend-otp", {
      method: "POST",
      body: request,
    });
  }

  async validateResetToken(token: string): Promise<{ email: string }> {
    const response = await $api<{ message: string; data: { email: string } }>(
      "/auth/validate-reset-token",
      {
        method: "POST",
        body: { token },
      },
    );
    return response.data;
  }

  async refreshToken(
    request: RefreshTokenRequestDTO,
  ): Promise<AuthResponseDTO> {
    const response = await $api<{ message: string; data: AuthResponseDTO }>(
      "/auth/refresh-token",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async logout(request: LogoutRequestDTO): Promise<void> {
    await $api("/auth/logout", {
      method: "POST",
      body: request,
    });
  }

  async switchAccount(
    request: SwitchAccountRequestDTO,
  ): Promise<AuthResponseDTO> {
    const response = await $api<{ message: string; data: AuthResponseDTO }>(
      "/auth/switch-account",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async fetchMe(): Promise<UserDTO> {
    const response = await $api<{ message: string; data: UserDTO }>(
      "/accounts/me",
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async validateTOTP(request: ValidateOTPRequestDTO): Promise<AuthResponseDTO> {
    // Deprecated: Convert to verifyOTP with method field
    return this.verifyOTP({
      email: request.email,
      code: request.code,
      method: "totp",
      device_id: request.device_id,
      remember_me: request.remember_me,
    });
  }

  async validateRecoveryCode(
    request: ValidateRecoveryCodeRequestDTO,
  ): Promise<AuthResponseDTO> {
    // Deprecated: Convert to verifyOTP with method field
    return this.verifyOTP({
      email: request.email,
      code: request.code,
      method: "recovery_code",
      device_id: request.device_id,
      remember_me: request.remember_me,
    });
  }
}
